import React from "react";
import PropTypes from "prop-types";
import { Avatar, Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
    DnsOutlined,
    PeopleAltOutlined,
    PersonOutline,
    PublicOutlined,
    SearchRounded,
} from "@mui/icons-material";
import { colors } from "../../theme/colors";
import utils from "../../universal/utils";
import {
    pickAvatarUpdatedAtMs,
    resolveAvatarSrc,
} from "../../utils/avatarUrl";
import { MOBILE_NAV_MQ, LOBBY_OURO_BORDER, LOBBY_OURO_SURFACE } from "./lobbyPageStyles";

const basePath = utils.getEdgeBasePath();

const LABEL_SX = {
    m: 0,
    mt: "0.28rem",
    fontSize: "0.52rem",
    fontWeight: 600,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: alpha(colors.text.secondary, 0.7),
    lineHeight: 1,
    whiteSpace: "nowrap",
};

const VALUE_SX = {
    m: 0,
    fontSize: { xs: "0.92rem", xl: "1.02rem" },
    fontWeight: 700,
    letterSpacing: "0.01em",
    color: alpha(colors.text.primary, 0.96),
    lineHeight: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
    textAlign: "inherit",
    fontVariantNumeric: "tabular-nums",
};

const RAIL_SX = {
    position: "relative",
    isolation: "isolate",
    width: "100%",
    maxWidth: { xs: "100%", xl: "52rem" },
    overflow: "hidden",
    borderRadius: "12px",
    border: `1px solid ${LOBBY_OURO_BORDER}`,
    background: LOBBY_OURO_SURFACE,
    backdropFilter: "blur(18px)",
    WebkitBackdropFilter: "blur(18px)",
    boxShadow: `
        inset 0 1px 0 rgba(255, 255, 255, 0.05),
        inset 0 -36px 64px rgba(246, 185, 76, 0.028)
    `,
    px: { xs: "0.55rem", xl: "0.75rem" },
    pt: { xs: "0.7rem", xl: "0.78rem" },
    pb: { xs: "0.55rem", xl: "0.62rem" },
    boxSizing: "border-box",
    "@keyframes lobbySignalLive": {
        "0%, 100%": { opacity: 0.72 },
        "50%": { opacity: 1 },
    },
};

function RailEyebrow() {
    return (
        <Typography
            component="p"
            sx={{
                m: 0,
                mb: { xs: "0.35rem", xl: "0.42rem" },
                textAlign: "center",
                fontSize: "0.54rem",
                fontWeight: 700,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: alpha(colors.text.secondary, 0.62),
                lineHeight: 1,
            }}
        >
            Queue status
        </Typography>
    );
}

const ICON_TILE_SX = {
    position: "relative",
    width: { xs: "1.8rem", xl: "1.95rem" },
    height: { xs: "1.8rem", xl: "1.95rem" },
    flexShrink: 0,
    display: "grid",
    placeItems: "center",
    borderRadius: "8px",
    border: `1px solid ${alpha(colors.primary.main, 0.18)}`,
    background: `
        linear-gradient(
            180deg,
            ${alpha(colors.primary.main, 0.12)},
            rgba(255,255,255,0.025)
        )
    `,
    boxShadow: `
        inset 0 1px 0 rgba(255,255,255,0.05),
        0 0 18px ${alpha(colors.primary.main, 0.07)}
    `,
    [MOBILE_NAV_MQ]: {
        width: "1.45rem",
        height: "1.45rem",
        borderRadius: "6px",
    },
};

function StatusIcon({ icon: Icon, color, isLive = false }) {
    return (
        <Box aria-hidden sx={ICON_TILE_SX}>
            <Icon
                sx={{
                    fontSize: { xs: "0.95rem", xl: "1.05rem" },
                    color: colors.accent.main,
                    [MOBILE_NAV_MQ]: {
                        fontSize: "0.8rem",
                    },
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    right: "-2px",
                    bottom: "-2px",
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    backgroundColor: color,
                    border: "2px solid rgba(8,8,10,0.96)",
                    boxShadow: `0 0 8px ${alpha(color, 0.5)}`,
                    animation: isLive
                        ? "lobbySignalLive 2.4s ease-in-out infinite"
                        : "none",
                }}
            />
        </Box>
    );
}

StatusIcon.propTypes = {
    icon: PropTypes.elementType.isRequired,
    color: PropTypes.string.isRequired,
    isLive: PropTypes.bool,
};

function StatCell({ label, icon, signal, isLive, children }) {
    return (
        <Box
            sx={{
                flex: "1 1 0",
                position: "relative",
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: "0.52rem", xl: "0.6rem" },
                minWidth: 0,
                px: { xs: "0.7rem", xl: "0.95rem" },
                py: { xs: "0.2rem", xl: "0.28rem" },
            }}
        >
            {icon ? (
                <StatusIcon icon={icon} color={signal} isLive={isLive} />
            ) : null}
            <Box
                sx={{
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: icon ? "flex-start" : "center",
                    justifyContent: "center",
                    textAlign: icon ? "left" : "center",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        minHeight: { xs: "1.2rem", xl: "1.35rem" },
                        minWidth: 0,
                        width: "100%",
                    }}
                >
                    {children}
                </Box>
                <Typography
                    component="p"
                    sx={{
                        ...LABEL_SX,
                        textAlign: icon ? "left" : "center",
                    }}
                >
                    {label}
                </Typography>
            </Box>
        </Box>
    );
}

StatCell.propTypes = {
    label: PropTypes.string.isRequired,
    icon: PropTypes.elementType,
    signal: PropTypes.string,
    isLive: PropTypes.bool,
    children: PropTypes.node,
};

function CellDivider() {
    return (
        <Box
            aria-hidden
            sx={{
                alignSelf: "stretch",
                width: "1px",
                my: { xs: "0.35rem", xl: "0.45rem" },
                flexShrink: 0,
                background: `
                    linear-gradient(
                        180deg,
                        transparent 0%,
                        ${alpha("#fff", 0.1)} 18%,
                        ${alpha("#fff", 0.14)} 50%,
                        ${alpha("#fff", 0.1)} 82%,
                        transparent 100%
                    )
                `,
            }}
        />
    );
}

function PartyAvatars({ members, maxPlayers, compact = false }) {
    const visible = (members || []).slice(0, 5);
    const countLabel = `${visible.length}/${maxPlayers || 5}`;
    const size = compact ? 22 : undefined;

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: compact ? "0.4rem" : "0.5rem",
                minWidth: 0,
                width: "100%",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    pl: "0.1rem",
                    flexShrink: 0,
                }}
            >
                {visible.map((member, index) => {
                    const nickname = member?.nickname || "?";
                    const initial =
                        String(nickname).trim().charAt(0).toUpperCase() || "?";
                    const avatarVersion = pickAvatarUpdatedAtMs(member);
                    const src = resolveAvatarSrc({
                        basePath,
                        picture: member?.picture,
                        userId: member?.userId,
                        avatarUpdatedAtMs: avatarVersion,
                        hasCustomAvatar: Boolean(member?.avatar),
                    });

                    return (
                        <Avatar
                            key={member?.userId || `${nickname}-${index}`}
                            alt={nickname}
                            src={src || undefined}
                            sx={{
                                width: size || { xs: 26, xl: 28 },
                                height: size || { xs: 26, xl: 28 },
                                fontSize: compact ? "0.6rem" : "0.7rem",
                                fontWeight: 700,
                                ml: index === 0 ? 0 : compact ? "-0.38rem" : "-0.45rem",
                                zIndex: visible.length - index,
                                border: `1.5px solid ${alpha(
                                    colors.background.dark,
                                    0.92
                                )}`,
                                boxShadow: `0 0 0 1px ${alpha(
                                    colors.primary.main,
                                    0.16
                                )}`,
                                background: `
                                    linear-gradient(
                                        160deg,
                                        ${alpha(colors.primary.main, 0.42)},
                                        ${alpha("#3a2a14", 0.95)}
                                    )
                                `,
                                color: colors.text.primary,
                            }}
                        >
                            {src ? null : initial || (
                                <PersonOutline sx={{ fontSize: compact ? 12 : 14 }} />
                            )}
                        </Avatar>
                    );
                })}
            </Box>
            <Typography
                component="p"
                sx={{
                    ...VALUE_SX,
                    fontSize: compact ? "0.78rem" : VALUE_SX.fontSize,
                }}
            >
                {countLabel}
            </Typography>
        </Box>
    );
}

PartyAvatars.propTypes = {
    members: PropTypes.arrayOf(PropTypes.object),
    maxPlayers: PropTypes.number,
    compact: PropTypes.bool,
};

function formatCount(value) {
    if (value === null || value === undefined) return "—";
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return String(Math.max(0, Math.round(n)));
}

function signalColor(value) {
    if (value === null || value === undefined) {
        return alpha(colors.text.primary, 0.96);
    }
    return Number(value) > 0
        ? colors.state.success.main
        : colors.primary.main;
}

function MobileStat({ label, value, accent = false, icon, signal }) {
    return (
        <Box
            sx={{
                flex: "1 1 0",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "0.38rem",
                px: "0.2rem",
            }}
        >
            {icon ? (
                <StatusIcon icon={icon} color={signal} isLive={accent} />
            ) : null}
            <Typography
                component="p"
                sx={{
                    m: 0,
                    fontSize: "1rem",
                    fontWeight: 700,
                    lineHeight: 1,
                    fontVariantNumeric: "tabular-nums",
                    color: accent
                        ? colors.state.success.main
                        : alpha(colors.text.primary, 0.96),
                }}
            >
                {value}
            </Typography>
            <Typography
                component="p"
                sx={{
                    m: 0,
                    fontSize: "0.62rem",
                    fontWeight: 600,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: alpha(colors.text.secondary, 0.82),
                    lineHeight: 1.2,
                    textAlign: "center",
                }}
            >
                {label}
            </Typography>
        </Box>
    );
}

MobileStat.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    accent: PropTypes.bool,
    icon: PropTypes.elementType,
    signal: PropTypes.string,
};

function MobileDetailRow({ label, value }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                gap: "0.75rem",
                px: "0.15rem",
            }}
        >
            <Typography
                component="span"
                sx={{
                    fontSize: "0.62rem",
                    fontWeight: 600,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: alpha(colors.text.secondary, 0.78),
                    lineHeight: 1.3,
                    flexShrink: 0,
                }}
            >
                {label}
            </Typography>
            <Typography
                component="span"
                sx={{
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    color: alpha(colors.text.primary, 0.94),
                    lineHeight: 1.3,
                    textAlign: "right",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                }}
            >
                {value}
            </Typography>
        </Box>
    );
}

MobileDetailRow.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

function MobileCompactBar({
    isParty,
    members,
    maxPlayers,
    regionsLabel,
    searchingValue,
    serversValue,
    searchingLive,
    fitValue,
    fitLive,
    mapsLabel,
}) {
    return (
        <Box
            role="status"
            aria-label="Matchmaking status"
            sx={{
                ...RAIL_SX,
                maxWidth: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "0.85rem",
                px: "0.9rem",
                pt: "0.85rem",
                pb: "0.85rem",
            }}
        >
            <RailEyebrow />
            {isParty ? (
                <PartyAvatars
                    members={members}
                    maxPlayers={maxPlayers}
                    compact
                />
            ) : null}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: "0.35rem",
                    width: "100%",
                }}
            >
                <MobileStat
                    label="Searching"
                    value={searchingValue}
                    accent={searchingLive}
                    icon={SearchRounded}
                    signal={signalColor(Number(searchingValue) || 0)}
                />
                <CellDivider />
                <MobileStat
                    label="In range"
                    value={fitValue}
                    accent={fitLive}
                    icon={PeopleAltOutlined}
                    signal={signalColor(Number(fitValue) || 0)}
                />
                <CellDivider />
                <MobileStat
                    label="Servers"
                    value={serversValue}
                    icon={DnsOutlined}
                    signal={signalColor(Number(serversValue) || 0)}
                />
            </Box>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.55rem",
                    pt: "0.15rem",
                    borderTop: `1px solid ${alpha("#fff", 0.06)}`,
                }}
            >
                <MobileDetailRow label="Maps" value={mapsLabel || "All maps"} />
                <MobileDetailRow
                    label="Region"
                    value={regionsLabel || "All regions"}
                />
            </Box>
        </Box>
    );
}

MobileCompactBar.propTypes = {
    isParty: PropTypes.bool,
    members: PropTypes.arrayOf(PropTypes.object),
    maxPlayers: PropTypes.number,
    regionsLabel: PropTypes.string,
    searchingValue: PropTypes.string,
    serversValue: PropTypes.string,
    searchingLive: PropTypes.bool,
    fitValue: PropTypes.string,
    fitLive: PropTypes.bool,
    mapsLabel: PropTypes.string,
};

export default function LobbySearchingInfoBar({
    isParty = false,
    members = [],
    maxPlayers = 5,
    regionsLabel = "All regions",
    playersSearching = null,
    serversOnline = null,
    compatibleCount = null,
    mapsLabel = "All maps",
}) {
    const searchingValue = formatCount(playersSearching);
    const serversValue = formatCount(serversOnline);
    const fitValue = formatCount(compatibleCount);
    const searchingLive =
        playersSearching != null && Number(playersSearching) > 0;
    const fitLive = compatibleCount != null && Number(compatibleCount) > 0;

    const searchingNode = (
        <Typography
            component="p"
            sx={{
                ...VALUE_SX,
                color: signalColor(playersSearching),
            }}
        >
            {searchingValue}
        </Typography>
    );

    const fitNode = (
        <Typography
            component="p"
            sx={{
                ...VALUE_SX,
                color: signalColor(compatibleCount),
            }}
        >
            {fitValue}
        </Typography>
    );

    const serversNode = (
        <Typography
            component="p"
            sx={{
                ...VALUE_SX,
                color: signalColor(serversOnline),
            }}
        >
            {serversValue}
        </Typography>
    );

    const regionNode = (
        <Typography component="p" sx={VALUE_SX}>
            {regionsLabel || "All regions"}
        </Typography>
    );

    const desktopCells = isParty
        ? [
              {
                  key: "party",
                  label: "Party",
                  node: (
                      <PartyAvatars members={members} maxPlayers={maxPlayers} />
                  ),
              },
              {
                  key: "searching",
                  label: "Searching",
                  icon: SearchRounded,
                  signal: signalColor(playersSearching),
                  isLive: searchingLive,
                  node: searchingNode,
              },
              {
                  key: "fit",
                  label: "In range",
                  icon: PeopleAltOutlined,
                  signal: signalColor(compatibleCount),
                  isLive: fitLive,
                  node: fitNode,
              },
              {
                  key: "region",
                  label: "Region",
                  icon: PublicOutlined,
                  signal: colors.primary.main,
                  node: regionNode,
              },
          ]
        : [
              {
                  key: "searching",
                  label: "Searching",
                  icon: SearchRounded,
                  signal: signalColor(playersSearching),
                  isLive: searchingLive,
                  node: searchingNode,
              },
              {
                  key: "fit",
                  label: "In range",
                  icon: PeopleAltOutlined,
                  signal: signalColor(compatibleCount),
                  isLive: fitLive,
                  node: fitNode,
              },
              {
                  key: "servers",
                  label: "Servers",
                  icon: DnsOutlined,
                  signal: signalColor(serversOnline),
                  isLive: serversOnline != null && Number(serversOnline) > 0,
                  node: serversNode,
              },
              {
                  key: "region",
                  label: "Region",
                  icon: PublicOutlined,
                  signal: colors.primary.main,
                  node: regionNode,
              },
          ];

    return (
        <>
            <Box
                sx={{
                    display: "none",
                    width: "100%",
                    [MOBILE_NAV_MQ]: {
                        display: "block",
                    },
                }}
            >
                <MobileCompactBar
                    isParty={isParty}
                    members={members}
                    maxPlayers={maxPlayers}
                    regionsLabel={regionsLabel}
                    searchingValue={searchingValue}
                    serversValue={serversValue}
                    searchingLive={searchingLive}
                    fitValue={fitValue}
                    fitLive={fitLive}
                    mapsLabel={mapsLabel}
                />
            </Box>

            <Box
                sx={{
                    ...RAIL_SX,
                    [MOBILE_NAV_MQ]: {
                        display: "none",
                    },
                }}
            >
                <RailEyebrow />
                <Box
                    role="status"
                    aria-label="Matchmaking status"
                    sx={{
                        display: "flex",
                        alignItems: "stretch",
                        justifyContent: "center",
                        width: "100%",
                    }}
                >
                    {desktopCells.map((cell, index) => (
                        <React.Fragment key={cell.key}>
                            {index > 0 && <CellDivider />}
                            <StatCell
                                label={cell.label}
                                icon={cell.icon}
                                signal={cell.signal}
                                isLive={cell.isLive}
                            >
                                {cell.node}
                            </StatCell>
                        </React.Fragment>
                    ))}
                </Box>
            </Box>
        </>
    );
}

LobbySearchingInfoBar.propTypes = {
    isParty: PropTypes.bool,
    members: PropTypes.arrayOf(PropTypes.object),
    maxPlayers: PropTypes.number,
    regionsLabel: PropTypes.string,
    averageElo: PropTypes.number,
    playersSearching: PropTypes.number,
    serversOnline: PropTypes.number,
    compatibleCount: PropTypes.number,
    mapsLabel: PropTypes.string,
};
